import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

const RegisterPage = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  
  // Master data
  const [cities, setCities] = useState([]);
  const [genders, setGenders] = useState([]);
  const [interests, setInterests] = useState([]);
  const [schools, setSchools] = useState([]);
  const [seriesList, setSeriesList] = useState([]);
  
  // Form data
  const [formData, setFormData] = useState({
    // Step 1: Akun
    email: '',
    username: '',
    password: '',
    confirmPassword: '',
    role: '', // UMUM, SISWA, GURU
    
    // Step 2: Info
    nama: '',
    nik: '',
    foto: '', // base64
    m_jeniskelamin_id: '',
    tgl_lahir: '',
    m_kota_id_lahir: '',
    
    // Step 3: Data Pribadi
    m_kota_id: '', // domisili
    jenjang: '', // SD, SMP, SMA
    m_sekolah_id: '',
    m_interest_id: '',
    nama_series: '', // Kota Series
    
    // Step 4: Kontak
    no_telp: '',
    instagram: '',
    tiktok: ''
  });
  
  const [showPassword, setShowPassword] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  
  // Fetch master data on mount
  useEffect(() => {
    fetchMasterData();
    fetchSeriesList();
  }, []);
  
  // Fetch schools when jenjang and nama_series changes
  useEffect(() => {
    if (formData.jenjang && formData.nama_series) {
      fetchSchools();
    }
  }, [formData.jenjang, formData.nama_series, seriesList]);
  
  const fetchMasterData = async () => {
    try {
      const [citiesRes, gendersRes, interestsRes] = await Promise.all([
        axios.get(`${API}/cities`),
        axios.get(`${API}/genders`),
        axios.get(`${API}/interests`)
      ]);
      
      setCities((citiesRes.data || []).sort((a, b) => (a.kota || '').localeCompare(b.kota || '')));
      setGenders(gendersRes.data);
      setInterests(interestsRes.data);
    } catch (error) {
      toast.error('Gagal memuat data master');
    }
  };
  
  const fetchSeriesList = async () => {
    try {
      const response = await axios.get(`${API}/series`);
      setSeriesList(response.data);
    } catch (error) {
      console.error('Error fetching series:', error);
    }
  };

  const fetchSchools = async () => {
    try {
      // Get kota from selected series or extract from nama_series
      const selectedSeries = seriesList.find(s => s.nama_series === formData.nama_series);
      let cityName = selectedSeries?.kota || formData.nama_series.replace(' Series', '');

      const response = await axios.get(`${API}/schools`);

      // Filter by city name and jenjang
      const filtered = response.data.filter(s => {
        const matchCity = s.kota === cityName;
        const matchJenjang = s.jenjang === formData.jenjang;
        return matchCity && matchJenjang;
      });

      setSchools(filtered);
    } catch (error) {
      console.error('Error fetching schools:', error);
      toast.error('Gagal memuat data sekolah');
    }
  };
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('File harus berupa gambar');
      return;
    }
    
    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Ukuran file maksimal 2MB');
      return;
    }
    
    // Convert to base64 with compression
    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      img.onload = () => {
        // Create canvas for compression
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        // Resize if too large
        let width = img.width;
        let height = img.height;
        const maxSize = 800;
        
        if (width > maxSize || height > maxSize) {
          if (width > height) {
            height = (height / width) * maxSize;
            width = maxSize;
          } else {
            width = (width / height) * maxSize;
            height = maxSize;
          }
        }
        
        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, 0, 0, width, height);
        
        // Convert to base64 with quality adjustment
        const base64 = canvas.toDataURL('image/jpeg', 0.7);
        
        // Check size (should be < 400KB)
        if (base64.length > 400 * 1024 * (4/3)) {
          toast.error('Gambar terlalu besar setelah kompresi. Pilih gambar lain.');
          return;
        }
        
        setFormData(prev => ({ ...prev, foto: base64 }));
        setImagePreview(base64);
      };
      img.src = event.target.result;
    };
    reader.readAsDataURL(file);
  };
  
  const validateStep = () => {
    const isGuru = formData.role === 'GURU';
    const isSiswa = formData.role === 'SISWA';
    const isGuruOrSiswa = isGuru || isSiswa;
    
    switch (currentStep) {
      case 1: // Akun
        if (!formData.email || !formData.username || !formData.password || !formData.confirmPassword || !formData.role) {
          toast.error('Semua field di Step Akun wajib diisi');
          return false;
        }
        if (formData.password.length < 6) {
          toast.error('Password minimal 6 karakter');
          return false;
        }
        if (formData.password !== formData.confirmPassword) {
          toast.error('Password dan konfirmasi password tidak cocok');
          return false;
        }
        break;
        
      case 2: // Info
        if (!formData.nama || !formData.m_jeniskelamin_id || !formData.tgl_lahir || !formData.m_kota_id_lahir) {
          toast.error('Field yang wajib: Nama lengkap, Jenis kelamin, Tanggal lahir, Kota kelahiran');
          return false;
        }
        // NIK & Foto mandatory for GURU/SISWA only
        if (isGuruOrSiswa) {
          if (!formData.nik) {
            toast.error('NIK wajib diisi untuk role Guru/Siswa');
            return false;
          }
          if (!formData.foto) {
            toast.error('Foto profil wajib diisi untuk role Guru/Siswa');
            return false;
          }
        }
        break;
        
      case 3: // Data Pribadi
        if (!formData.m_kota_id || !formData.jenjang || !formData.m_interest_id || !formData.nama_series) {
          toast.error('Field yang wajib: Kota domisili, Jenjang sekolah, Minat, Kota Series');
          return false;
        }
        // Nama sekolah mandatory for GURU/SISWA only
        if (isGuruOrSiswa && !formData.m_sekolah_id) {
          toast.error('Nama sekolah wajib diisi untuk role Guru/Siswa');
          return false;
        }
        break;
        
      case 4: // Kontak
        if (!formData.no_telp) {
          toast.error('No telepon wajib diisi');
          return false;
        }
        break;
        
      default:
        break;
    }
    
    return true;
  };
  
  const handleNext = () => {
    if (validateStep()) {
      setCurrentStep(prev => Math.min(prev + 1, 5));
    }
  };
  
  const handlePrev = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };
  
  const handleSubmit = async () => {
    if (!validateStep()) return;
    
    setLoading(true);
    
    try {
      const registerData = {
        email: formData.email,
        username: formData.username,
        password: formData.password,
        nama: formData.nama,
        role: formData.role,
        no_telp: formData.no_telp,
        nik: formData.nik || null,
        foto: formData.foto || null,
        m_jeniskelamin_id: formData.m_jeniskelamin_id,
        tgl_lahir: formData.tgl_lahir,
        m_kota_id_lahir: formData.m_kota_id_lahir,
        m_kota_id: formData.m_kota_id,
        m_sekolah_id: formData.m_sekolah_id || null,
        m_interest_id: formData.m_interest_id,
        instagram: formData.instagram || null,
        tiktok: formData.tiktok || null,
        nama_series: formData.nama_series
      };
      
      const response = await axios.post(`${API}/auth/register`, registerData);
      toast.success('Registrasi berhasil! Silakan cek email untuk verifikasi.');
      navigate('/verify-email', { state: { email: formData.email } });
      
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Registrasi gagal';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };
  
  const renderStepIndicator = () => {
    const steps = ['Akun', 'Personal', 'Info', 'Kontak', 'Verifikasi'];
    
    return (
      <div className="flex items-center justify-center mb-8">
        {steps.map((step, index) => (
          <React.Fragment key={index}>
            <div className="flex flex-col items-center">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center font-bold ${
                currentStep === index + 1 
                  ? 'bg-ltb-bright text-white' 
                  : currentStep > index + 1
                  ? 'bg-green-500 text-white'
                  : 'bg-gray-300 text-gray-600'
              }`}>
                {currentStep > index + 1 ? '✓' : index + 1}
              </div>
              <span className={`text-xs mt-2 ${
                currentStep === index + 1 ? 'text-ltb-bright font-semibold' : 'text-gray-500'
              }`}>
                {step}
              </span>
            </div>
            {index < steps.length - 1 && (
              <div className={`w-16 h-1 mx-2 ${
                currentStep > index + 1 ? 'bg-green-500' : 'bg-gray-300'
              }`} />
            )}
          </React.Fragment>
        ))}
      </div>
    );
  };
  
  const renderStep1 = () => (
    <div className="space-y-4">
      <h2 className="text-2xl font-bold text-gray-800 mb-4">Buat Akun</h2>
      
      {/* Role Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Pilih Role <span className="text-red-500">*</span>
        </label>
        <div className="grid grid-cols-3 gap-3">
          {['UMUM', 'SISWA', 'GURU'].map((role) => (
            <button
              key={role}
              type="button"
              onClick={() => setFormData({...formData, role})}
              className={`p-4 border-2 rounded-lg text-center transition-all ${
                formData.role === role
                  ? 'border-ltb-bright bg-ltb-bright text-white'
                  : 'border-gray-300 hover:border-ltb-bright'
              }`}
            >
              <div className="text-2xl mb-1">
                {role === 'UMUM' ? '👤' : role === 'SISWA' ? '🎓' : '👨‍🏫'}
              </div>
              <div className="text-sm font-semibold">{role}</div>
            </button>
          ))}
        </div>
      </div>
      
      {/* Email */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Email <span className="text-red-500">*</span>
        </label>
        <input
          type="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ltb-bright focus:border-transparent"
          placeholder=""
        />
      </div>
      
      {/* Username */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Username <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          name="username"
          value={formData.username}
          onChange={handleChange}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ltb-bright focus:border-transparent"
          placeholder="username"
        />
      </div>
      
      {/* Password */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Password <span className="text-red-500">*</span>
        </label>
        <div className="relative">
          <input
            type={showPassword ? "text" : "password"}
            name="password"
            value={formData.password}
            onChange={handleChange}
            className="w-full px-4 py-2 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ltb-bright focus:border-transparent"
            placeholder="Minimal 6 karakter"
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400"
          >
            {showPassword ? '👁️' : '👁️‍🗨️'}
          </button>
        </div>
      </div>
      
      {/* Confirm Password */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Konfirmasi Password <span className="text-red-500">*</span>
        </label>
        <input
          type={showPassword ? "text" : "password"}
          name="confirmPassword"
          value={formData.confirmPassword}
          onChange={handleChange}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ltb-bright focus:border-transparent"
          placeholder="Ketik ulang password"
        />
      </div>
    </div>
  );
  
  const renderStep2 = () => {
    const isGuruOrSiswa = formData.role === 'GURU' || formData.role === 'SISWA';
    
    return (
      <div className="space-y-4">
        <h2 className="text-2xl font-bold text-gray-800 mb-4">Personal</h2>
        
        {/* Nama Lengkap */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Nama Lengkap <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name="nama"
            value={formData.nama}
            onChange={handleChange}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ltb-bright focus:border-transparent"
            placeholder="Nama lengkap Anda"
          />
        </div>
        
        {/* NIK */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            NIK {isGuruOrSiswa && <span className="text-red-500">*</span>}
          </label>
          <input
            type="text"
            name="nik"
            value={formData.nik}
            onChange={handleChange}
            maxLength={16}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ltb-bright focus:border-transparent"
            placeholder="16 digit NIK"
          />
        </div>
        
        {/* Foto Profil */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Foto Profil {isGuruOrSiswa && <span className="text-red-500">*</span>}
          </label>
          <div className="flex items-center space-x-4">
            {imagePreview && (
              <img src={imagePreview} alt="Preview" className="w-20 h-20 rounded-full object-cover" />
            )}
            <input
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ltb-bright focus:border-transparent"
            />
          </div>
          <p className="text-xs text-gray-500 mt-1">Max 2MB</p>
        </div>
        
        {/* Jenis Kelamin */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Jenis Kelamin <span className="text-red-500">*</span>
          </label>
          <select
            name="m_jeniskelamin_id"
            value={formData.m_jeniskelamin_id}
            onChange={handleChange}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ltb-bright focus:border-transparent"
          >
            <option value="">Pilih jenis kelamin</option>
            {genders.map(g => (
              <option key={g.id} value={g.id}>{g.jeniskelamin}</option>
            ))}
          </select>
        </div>
        
        {/* Tanggal Lahir */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Tanggal Lahir <span className="text-red-500">*</span>
          </label>
          <input
            type="date"
            name="tgl_lahir"
            value={formData.tgl_lahir}
            onChange={handleChange}
            max={new Date().toISOString().split('T')[0]}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ltb-bright focus:border-transparent"
          />
        </div>
        
        {/* Kota Kelahiran */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Kota Kelahiran <span className="text-red-500">*</span>
          </label>
          <select
            name="m_kota_id_lahir"
            value={formData.m_kota_id_lahir}
            onChange={handleChange}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ltb-bright focus:border-transparent"
          >
            <option value="">Pilih kota kelahiran</option>
            {cities.map(c => (
              <option key={c.id} value={c.id}>{c.kota}</option>
            ))}
          </select>
        </div>
      </div>
    );
  };
  
  const renderStep3 = () => {
    const isGuruOrSiswa = formData.role === 'GURU' || formData.role === 'SISWA';
    
    return (
      <div className="space-y-4">
        <h2 className="text-2xl font-bold text-gray-800 mb-4">Info</h2>
        
        {/* Kota Domisili */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Kota Domisili <span className="text-red-500">*</span>
          </label>
          <select
            name="m_kota_id"
            value={formData.m_kota_id}
            onChange={handleChange}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ltb-bright focus:border-transparent"
          >
            <option value="">Pilih kota domisili</option>
            {cities.map(c => (
              <option key={c.id} value={c.id}>{c.kota}</option>
            ))}
          </select>
        </div>
        
        {/* Kota Series */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Kota Series <span className="text-red-500">*</span>
          </label>
          <select
            name="nama_series"
            value={formData.nama_series}
            onChange={handleChange}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ltb-bright focus:border-transparent"
          >
            <option value="">Pilih kota series</option>
            {seriesList.map(s => (
              <option key={s.id} value={s.nama_series}>{s.kota ? `${s.kota} Series` : s.nama_series}</option>
            ))}
          </select>
        </div>
        
        {/* Jenjang Sekolah */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Jenjang <span className="text-red-500">*</span>
          </label>
          <select
            name="jenjang"
            value={formData.jenjang}
            onChange={handleChange}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ltb-bright focus:border-transparent"
          >
            <option value="">Pilih jenjang</option>
            <option value="SD">SD/MI</option>
            <option value="SMP">SMP/MTs</option>
            <option value="SMA">SMA/SMK/MA</option>
          </select>
        </div>
        
        {/* Nama Sekolah */}
        {formData.jenjang && formData.nama_series ? (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nama Sekolah {isGuruOrSiswa && <span className="text-red-500">*</span>}
            </label>
            {schools.length > 0 ? (
              <select
                name="m_sekolah_id"
                value={formData.m_sekolah_id}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ltb-bright focus:border-transparent"
              >
                <option value="">Pilih nama sekolah</option>
                {schools.map(s => (
                  <option key={s.id} value={s.id}>{s.nama_sekolah}</option>
                ))}
              </select>
            ) : (
              <p className="text-sm text-yellow-600 bg-yellow-50 p-3 rounded-lg">
                ℹ️ Tidak ada sekolah {formData.jenjang} di kota series yang dipilih
              </p>
            )}
          </div>
        ) : (
          formData.nama_series && !formData.jenjang && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nama Sekolah {isGuruOrSiswa && <span className="text-red-500">*</span>}
              </label>
              <p className="text-sm text-yellow-600 bg-yellow-50 p-3 rounded-lg">
                ⚠️ Pilih jenjang terlebih dahulu untuk melihat daftar sekolah
              </p>
            </div>
          )
        )}
        {!formData.nama_series && formData.jenjang && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nama Sekolah {isGuruOrSiswa && <span className="text-red-500">*</span>}
            </label>
            <p className="text-sm text-yellow-600 bg-yellow-50 p-3 rounded-lg">
              ⚠️ Pilih kota series terlebih dahulu untuk melihat daftar sekolah
            </p>
          </div>
        )}
        
        {/* Minat */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Minat <span className="text-red-500">*</span>
          </label>
          <select
            name="m_interest_id"
            value={formData.m_interest_id}
            onChange={handleChange}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ltb-bright focus:border-transparent"
          >
            <option value="">Pilih minat</option>
            {interests.map(i => (
              <option key={i.id} value={i.id}>{i.interest}</option>
            ))}
          </select>
        </div>
      </div>
    );
  };
  
  const renderStep4 = () => (
    <div className="space-y-4">
      <h2 className="text-2xl font-bold text-gray-800 mb-4">Kontak</h2>
      
      {/* No Telepon */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          No. Telepon <span className="text-red-500">*</span>
        </label>
        <input
          type="tel"
          name="no_telp"
          value={formData.no_telp}
          onChange={handleChange}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ltb-bright focus:border-transparent"
          placeholder="08xxxxxxxxxx"
        />
      </div>
      
      {/* Instagram */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Instagram
        </label>
        <input
          type="text"
          name="instagram"
          value={formData.instagram}
          onChange={handleChange}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ltb-bright focus:border-transparent"
          placeholder="@username"
        />
      </div>
      
      {/* TikTok */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          TikTok
        </label>
        <input
          type="text"
          name="tiktok"
          value={formData.tiktok}
          onChange={handleChange}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ltb-bright focus:border-transparent"
          placeholder="@username"
        />
      </div>
    </div>
  );
  
  const renderStep5 = () => (
    <div className="space-y-4">
      <h2 className="text-2xl font-bold text-gray-800 mb-4">Verifikasi</h2>
      
      <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
        <svg className="w-16 h-16 text-green-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <h3 className="text-xl font-bold text-gray-800 mb-2">Data Lengkap!</h3>
        <p className="text-gray-600 mb-4">
          Klik tombol di bawah untuk menyelesaikan registrasi.
        </p>
        <p className="text-sm text-gray-500">
          Kode verifikasi akan dikirim ke email: <strong>{formData.email}</strong>
        </p>
      </div>
    </div>
  );
  
  return (
    <div className="min-h-screen bg-gradient-ltb-br flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl shadow-2xl p-8 w-full max-w-2xl">
        {/* Back to Home */}
        <div className="mb-4">
          <button
            onClick={() => navigate('/')}
            className="text-ltb-bright hover:text-ltb-deep flex items-center gap-1 text-sm font-semibold"
          >
            ← Kembali ke Halaman Utama
          </button>
        </div>
        
        {/* Header */}
        <div className="text-center mb-6">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">Registrasi</h1>
          <p className="text-gray-600">LTB+</p>
        </div>
        
        {/* Step Indicator */}
        {renderStepIndicator()}
        
        {/* Form Content */}
        <div className="min-h-[400px]">
          {currentStep === 1 && renderStep1()}
          {currentStep === 2 && renderStep2()}
          {currentStep === 3 && renderStep3()}
          {currentStep === 4 && renderStep4()}
          {currentStep === 5 && renderStep5()}
        </div>
        
        {/* Navigation Buttons */}
        <div className="flex justify-between mt-8">
          {currentStep > 1 && (
            <button
              onClick={handlePrev}
              className="px-6 py-2 border-2 border-ltb-bright text-ltb-bright rounded-lg font-semibold hover:bg-ltb-bright hover:text-white transition-all"
            >
              ← Kembali
            </button>
          )}
          
          <div className="ml-auto">
            {currentStep < 5 ? (
              <button
                onClick={handleNext}
                className="px-6 py-2 bg-gradient-ltb-r text-white rounded-lg font-semibold hover:shadow-xl transition-all"
              >
                Berikutnya →
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={loading}
                className="px-8 py-3 bg-gradient-ltb-r text-white rounded-lg font-semibold hover:shadow-xl transition-all disabled:opacity-50"
              >
                {loading ? 'Mendaftar...' : 'Daftar'}
              </button>
            )}
          </div>
        </div>
        
        {/* Footer */}
        <div className="mt-6 text-center space-y-2">
          <p className="text-gray-600">
            Sudah punya akun?{' '}
            <button
              onClick={() => navigate('/login')}
              className="text-ltb-bright hover:text-ltb-medium font-semibold"
            >
              Login
            </button>
          </p>
          <p className="text-gray-600">
            Butuh bantuan?{' '}
            <button
              onClick={() => navigate('/faq')}
              className="text-ltb-bright hover:text-ltb-medium font-semibold"
            >
              Lihat FAQ
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
